import { Dispatch } from '@reduxjs/toolkit';
import { groupBy, uniqBy } from 'lodash/fp';

import SessionClientDashboard from 'dashboard/lib/clients/SessionClientDashboard';
import { Environment } from 'dashboard/models/environment';
import { Event, RedactedObjectPaths } from 'dashboard/models/event';
import {
  EventsFilterParams,
  setEvents,
  setRedactedObjectPaths,
} from 'dashboard/redux/actions/events';
import { DashboardThunkAction } from 'dashboard/redux/types';
import { EventName } from 'lib/constants/events';
import { ApiEndpoint, PaginationParams } from 'lib/interfaces/api';

export function loadEventList({
  currentEnvironment,
  currentPage,
  pageSize,
  filters,
  abortSignal,
}: {
  currentEnvironment: Environment;
  currentPage: number;
  pageSize: number;
  filters: EventsFilterParams;
  abortSignal?: AbortSignal;
}) {
  return async (dispatch: Dispatch) => {
    const paramsFull = {
      ...createParamsForList(currentEnvironment, filters),
      page: {
        number: currentPage,
        size: pageSize,
      },
      signal: abortSignal,
    };

    const { data: events, meta: paginationParams } = await SessionClientDashboard.listResource<
      Event,
      PaginationParams
    >(ApiEndpoint.Event, paramsFull);

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    dispatch(setEvents(events, currentEnvironment, paginationParams));
  };
}

function createParamsForList(currentEnvironment: Environment, filters: EventsFilterParams) {
  const { createdAtStart, createdAtEnd, omni, names } = filters;

  return {
    filter: {
      'environment-id': currentEnvironment.id,
      omni,
      names,
      'created-at-start': createdAtStart && createdAtStart.toString(),
      'created-at-end': createdAtEnd && createdAtEnd.toString(),
    },
  };
}

export const loadRedactedObjectPaths =
  (ids: string[], eventName: EventName): DashboardThunkAction<Promise<void>> =>
  async (dispatch, getState) => {
    // TODO: Should loop and grab all events. Currently this is brittle.
    // TODO: this causes an unhandledpromiserejection error for txns
    const environmentId = getState().currentEnvironment.currentEnvironment.id;
    const { data: events } = await SessionClientDashboard.listResource<Event, PaginationParams>(
      ApiEndpoint.Event,
      {
        filter: {
          'environment-id': environmentId,
          'object-id': ids,
          names: [eventName],
        },
      },
    );

    const groupedEvents = groupBy((event) => event.attributes.payload.data.id, events);

    const redactedObjectPaths = ids.reduce<RedactedObjectPaths>(
      (paths, id) => ({
        ...paths,
        [id]: uniqBy(
          'path',
          groupedEvents[id]?.flatMap((event) => {
            const eventRedactions = event.attributes?.context?.['redacted-object-paths'] ?? {};
            return Object.entries(eventRedactions).map(([path, data]) => ({
              path,
              'redacted-at': data['redacted-at'],
            }));
          }),
        ),
      }),
      {},
    );

    const currentRedactedObjectPaths = getState().events.redactedObjectPaths;
    dispatch(setRedactedObjectPaths({ ...currentRedactedObjectPaths, ...redactedObjectPaths }));
  };
